// Miembros (ChannelMembersSheet.swift): admins first, then the alphabet. Server-first both ways —
// the roster is refetched after every accepted change — and the verbs are offered only when this
// reader's roles or resolved permissions carry them (ChannelMembersPresentation.canManageMembers),
// and the Member ▾ / Admin ▾ menu only with `manage_channel_roles` (canManageRoles): a hidden button
// beats one that answers 403. The reader's own row never offers "remove": leaving is a different
// verb, and it lives on Información. A group message has no roster to edit; it offers what it
// CAN do — open the conversation for a larger set of people.
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { data } from '../../../bridge/client';
import { ChannelMembersPresentation, type Row } from '../../../core/channelMembersPresentation';
import type { ChannelRecord } from '../../../core/records';
import type { ChannelPanelSnapshot, Roster } from '../../../data/features/channelPanel';
import { t, tf } from '../../../i18n/i18n';
import { Avatar } from '../../Avatar';
import { Button, ConfirmButton, EmptyState, Group, InlineStatus, SearchInput } from '../../kit/Kit';
import { useShell } from '../../shell/ShellContext';
import { AddPeopleDialog } from './AddPeopleDialog';
import { ConvertGroupDialog } from '../convertGroup/ConvertGroupDialog';
import { usersById } from './panelModel';

export function MembersTab({ snap, channel }: { snap: ChannelPanelSnapshot; channel: ChannelRecord }) {
  const shell = useShell();
  const me = snap.currentUserId ?? '';
  const [roster, setRoster] = useState<Roster | null>(null);
  const [status, setStatus] = useState<'loading' | 'idle' | 'failed'>('loading');
  const [filter, setFilter] = useState('');
  const [adding, setAdding] = useState(false);
  const [converting, setConverting] = useState(false);

  const load = useCallback(() => {
    setStatus('loading');
    return data.call('channelRoster', channel.id).then(
      (fresh) => {
        if (fresh) setRoster(fresh);
        setStatus(fresh ? 'idle' : 'failed');
      },
      () => setStatus('failed'),
    );
  }, [channel.id]);

  useEffect(() => {
    let alive = true;
    // What this device last knew paints first; the server's roster replaces it.
    void data.call('channelRosterCached', channel.id).then((cached) => alive && setRoster((r) => r ?? (cached.members.length ? cached : null)));
    void load();
    return () => {
      alive = false;
    };
  }, [channel.id, load]);

  const rows = useMemo(
    () =>
      roster
        ? ChannelMembersPresentation.rows({ members: roster.members, usersById: usersById(roster.users), currentUserId: me, unknownLabel: t('channel.reaction.unknown') })
        : [],
    [roster, me],
  );
  const ownRoles = roster?.fresh ? (roster.members.find((m) => m.userId === me)?.roles ?? '') : '';
  // System + team + channel grants (shared/API.md §26.1): what lets a team or system admin manage a
  // private channel they are not channel admin of, and hand out channel admin at all.
  const permissions = useMemo(() => new Set(snap.permissions), [snap.permissions]);
  const canManage = !!roster?.fresh && channel.deleteAt === 0 && ChannelMembersPresentation.canManageMembers(channel.type, ownRoles, permissions);
  const canManageRoles = !!roster?.fresh && channel.deleteAt === 0 && ChannelMembersPresentation.canManageRoles(channel.type, permissions);
  const [roleBusy, setRoleBusy] = useState<string | null>(null);
  const group = ChannelMembersPresentation.groupOptions(channel.type, rows.length);

  const needle = filter.trim().toLowerCase().replace(/^@/, '');
  const visible = needle ? rows.filter((r) => r.displayName.toLowerCase().includes(needle) || r.username.toLowerCase().includes(needle)) : rows;

  const remove = (row: Row) => {
    data.call('channelRemoveMember', channel.id, row.userId).then(
      (ok) => (ok ? void load() : shell.toast(t('people.removeFailed'), 'error')),
      () => shell.toast(t('people.removeFailed'), 'error'),
    );
  };

  // Server-first, like add and remove: the badge moves when the refetched roster says so.
  const setAdmin = (row: Row, isAdmin: boolean) => {
    if (roleBusy) return;
    setRoleBusy(row.userId);
    data
      .call('channelSetMemberAdmin', channel.id, row.userId, isAdmin)
      .then(
        (ok) => (ok ? load() : shell.toast(t('people.roleChangeFailed'), 'error')),
        () => shell.toast(t('people.roleChangeFailed'), 'error'),
      )
      .finally(() => setRoleBusy(null));
  };

  const open = (row: Row, e: MouseEvent | KeyboardEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    shell.openUser({ userId: row.userId }, { x: rect.left, y: rect.top + rect.height / 2 });
  };

  return (
    <>
      <div className="cp-toolbar">
        <SearchInput value={filter} onChange={setFilter} placeholder={t('web.channelPanel.membersFilter')} />
        {canManage ? (
          <Button kind="primary" icon="plus" onClick={() => setAdding(true)}>
            {t('people.add')}
          </Button>
        ) : null}
      </div>
      {status === 'failed' ? <InlineStatus kind="error" text={t('web.channelPanel.rosterFailed')} onRetry={() => void load()} /> : null}
      {!roster && status === 'loading' ? <InlineStatus kind="loading" text={t('channel.loading.accessibility')} /> : null}

      {roster && !visible.length && needle ? <EmptyState title={tf('web.channelPanel.membersNoMatch', filter.trim())} /> : null}
      {visible.length ? (
        <Group title={snap.memberCount !== null && !needle ? tf('channel.info.members', snap.memberCount) : undefined}>
          {visible.map((row) => (
            <div
              key={row.userId}
              className="list-row clickable cp-member"
              role="button"
              tabIndex={0}
              onClick={(e) => open(row, e)}
              onKeyDown={(e) => e.key === 'Enter' && open(row, e)}
            >
              <span className="list-row-leading">
                <Avatar userId={row.userId} name={row.displayName} size={32} version={row.lastPictureUpdate} ring />
              </span>
              <span className="list-row-text">
                <span className="list-row-title">{row.isSelf ? tf('people.yourself', row.displayName) : row.displayName}</span>
                {row.username ? <span className="list-row-subtitle">@{row.username}</span> : null}
              </span>
              <span className="list-row-trailing" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                {ChannelMembersPresentation.offersRoleChange(row, canManageRoles) ? (
                  <RoleMenu isAdmin={row.isChannelAdmin} busy={roleBusy === row.userId} disabled={roleBusy !== null} onPick={(admin) => setAdmin(row, admin)} />
                ) : row.isChannelAdmin ? (
                  <span className="cp-badge">{t('people.admin')}</span>
                ) : null}
                {canManage && !row.isSelf ? <ConfirmButton label={t('people.remove')} confirmLabel={t('web.channelPanel.removeConfirm')} onConfirm={() => remove(row)} /> : null}
              </span>
            </div>
          ))}
        </Group>
      ) : null}

      {channel.type === 'G' && roster ? (
        <Group>
          {group.canAddPeople ? (
            <button className="list-row clickable" onClick={() => setAdding(true)}>
              <span className="list-row-text">
                <span className="list-row-title cp-accent">{t('people.groupAdd')}</span>
                <span className="list-row-subtitle">{tf('people.groupSeats', group.freeSeats)}</span>
              </span>
            </button>
          ) : null}
          <div className="cp-note">{group.canAddPeople ? t('people.groupNote') : tf('people.groupFull', ChannelMembersPresentation.maxGroupMembers)}</div>
          {group.canConvertToChannel ? (
            <button className="list-row clickable" onClick={() => setConverting(true)}>
              <span className="list-row-text">
                <span className="list-row-title cp-accent">{t('people.groupConvert')}</span>
                <span className="list-row-subtitle">{t('people.groupConvertNote')}</span>
              </span>
            </button>
          ) : null}
        </Group>
      ) : null}

      {converting ? (
        <ConvertGroupDialog
          channelId={channel.id}
          groupTitle={rows.filter((r) => !r.isSelf).map((r) => r.displayName).join(', ')}
          onClose={() => setConverting(false)}
          onConverted={() => {
            setConverting(false);
            shell.toast(t('web.groupConversion.done'));
          }}
        />
      ) : null}

      {adding ? (
        <AddPeopleDialog
          channel={channel}
          teamId={channel.teamId || shell.team?.id || ''}
          existingIds={new Set(roster?.members.map((m) => m.userId) ?? [])}
          maxSelectable={channel.type === 'G' ? group.freeSeats : null}
          onClose={() => setAdding(false)}
          onAdded={() => void load()}
        />
      ) : null}
    </>
  );
}

/**
 * The member's role as a menu — the desktop's Member ▾ / Admin ▾ control in its members list, for a
 * reader holding `manage_channel_roles` (user, 2026-10-12: a system admin's powers). The chip names
 * the CURRENT role, so it doubles as the badge it replaces; picking the current role changes nothing.
 * A portal, like the message menu: the panel's scroller would clip a menu drawn inside it.
 */
function RoleMenu({ isAdmin, busy, disabled, onPick }: { isAdmin: boolean; busy: boolean; disabled: boolean; onPick(admin: boolean): void }) {
  const chip = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [at, setAt] = useState<{ right: number; top: number } | null>(null);
  useEffect(() => {
    if (!at) return;
    const close = (e: globalThis.MouseEvent) => !menu.current?.contains(e.target as Node) && !chip.current?.contains(e.target as Node) && setAt(null);
    const esc = (e: globalThis.KeyboardEvent) => e.key === 'Escape' && setAt(null);
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', esc);
    };
  }, [at]);
  const choose = (admin: boolean) => {
    setAt(null);
    if (admin !== isAdmin) onPick(admin);
  };
  return (
    <>
      <button
        ref={chip}
        className={`cp-role${isAdmin ? ' admin' : ''}${busy ? ' busy' : ''}`}
        aria-haspopup="menu"
        aria-expanded={!!at}
        disabled={disabled}
        onClick={() => {
          const r = chip.current?.getBoundingClientRect();
          if (r) setAt((cur) => (cur ? null : { right: window.innerWidth - r.right, top: r.bottom + 4 }));
        }}
      >
        {isAdmin ? t('people.roleAdminMenu') : t('people.roleMemberMenu')}
      </button>
      {at
        ? createPortal(
            <div ref={menu} className="popover cp-role-menu" role="menu" style={{ position: 'fixed', right: at.right, top: at.top, minWidth: 180 }}>
              <button className="menu-item" role="menuitemradio" aria-checked={!isAdmin} onClick={() => choose(false)}>
                <span className="cp-role-check">{!isAdmin ? '✓' : ''}</span> {t('people.roleMember')}
              </button>
              <button className="menu-item" role="menuitemradio" aria-checked={isAdmin} onClick={() => choose(true)}>
                <span className="cp-role-check">{isAdmin ? '✓' : ''}</span> {t('people.roleAdmin')}
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
